import api from '../../api';
import Banca, { BancaUtils, TipoBanca, DataDeBanca, StatusBanca } from '../../model/banca/Banca';
import { BancaDoDia } from '../../model/banca/BancasDoDia';
import MembroBanca, { StatusMembroNome } from '../../model/banca/MembroBanca';
import { BancaNovaResponse, BancasNovaApiResponse } from '../../model/banca/BancasNovaResponse';

export interface BancaResponseTecnicoPage {
    processoId: string;
    bancaId: string;
    tipoBanca: TipoBanca;
    dataDeBancaResponse: DataDeBanca;
    numeroCandidatosComVotacaoFechada: number;
    processoNome: string;
}

export interface BancasMembroResponse {
    bancas: BancaMembro[];
}

export interface BancaMembro {
    id: string;
    processoId: string;
    status: string;
    processoNome: string;
    tipoBanca: string;
    membros: string[];
    data: string;
    horaInicial: string;
    horaFinal: string;
    salaDeBanca: string;
    dataFinalizacao?: string;
}

export interface AtaValidacaoAnteriorResumoResponse {
    totalCandidatosValidacaoAnterior: number;
    todasBancasEncerradas: boolean;
    podeGerarAta: boolean;
}

export class BancaViewModel {

    static async getBancasTecnicoPage(): Promise<BancaResponseTecnicoPage[]> {
        try {
            const response = await api.get<BancaResponseTecnicoPage[]>('/tecnico/bancas');
            if (response.status === 200) {
                return response.data.map((banca) => ({
                    processoId: banca.processoId,
                    bancaId: banca.bancaId,
                    tipoBanca: banca.tipoBanca,
                    dataDeBancaResponse: banca.dataDeBancaResponse,
                    numeroCandidatosComVotacaoFechada: banca.numeroCandidatosComVotacaoFechada,
                    processoNome: banca.processoNome
                }) as BancaResponseTecnicoPage); // Assegurando que o objeto esteja de acordo com a interface
            } else {
                throw new Error(`Erro ao buscar bancas: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Erro ao buscar bancas técnicos:', error);
            throw error;
        }
    }

    static async cancelarBanca(bancaId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/cancelamento`);
        }
        catch (error) {
            throw error;
        }
    }

    static async fecharBanca(bancaId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/fechamento`);
        }
        catch (error) {
            throw error;
        }
    }

    static async alternarFormaRealizacao(bancaId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/forma-realizacao`);
        }
        catch (error) {
            throw error;
        }
    }


    static async finalizarBanca(bancaId: string, ocorrencia: string): Promise<{ data: Banca, status: number }> {
        const body = {
            ocorrencia: ocorrencia
        };

        const response = await api.patch(`/bancas/${bancaId}/finalizacao`, body);
        return { data: response.data, status: response.status };
    }

    static async finalizarBancaSemAta(bancaId: string, ocorrencia: string): Promise<{ data: Banca, status: number }> {
        const body = {
            ocorrencia: ocorrencia
        };

        const response = await api.patch(`/bancas/${bancaId}/finalizacao-sem-ata`, body);
        return { data: response.data, status: response.status };
    }

    static async criarAta(bancaId: string): Promise<void> {
        await api.post(`/bancas/${bancaId}/ata`);
    }

    static async finalizarBancaIntegracao(bancaId: string, ocorrencia: string): Promise<{ data: Banca, status: number }> {
        const body = {
            ocorrencia: ocorrencia
        };

        const response = await api.patch(`/bancas/${bancaId}/finalizacao-integracao`, body);
        return { data: response.data, status: response.status };
    }

    static async finalizarIntegracao(bancaId: string): Promise<void> {
        const body = {
            ocorrencia: "Não se aplica"
        };
        try {
            await api.patch(`/bancas/${bancaId}/finalizacao-integracao`, body);
        }
        catch (error) {
            throw error;
        }
    }

    static async abrirIntegracao(bancaId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/abrir-integracao`);
        }
        catch (error) {
            throw error;
        }
    }

    static async alternarHasIntegracao(bancaId: string): Promise<void> {
        try {
            await api.patch(`/bancas/${bancaId}/alternar-integracao`);
        }
        catch (error) {
            throw error;
        }
    }

    static async publicarEventoIntegracaoSiga(bancaId: string): Promise<void> {
        try {
            await api.post(`/bancas/${bancaId}/publicar-integracao-siga`);
        }
        catch (error) {
            throw error;
        }
    }

    static async getBancasMembro(): Promise<Banca[]> {
        try {
            const response = await api.get('/bancas/membro');

            const bancas = response.data?.map((data: any) => ({
                id: data.bancaId,
                status: BancaUtils.mapStringToStatusBanca(data.status as unknown as string),
                tipoBanca: {
                    id: data.tipoBanca.id,
                    nome: BancaUtils.mapStringToTipoBancaNome(data.tipoBanca.nome)
                },

                salaDeBanca: {
                    id: data.salaDeBanca.id,
                    link: data.salaDeBanca.link
                },
                dataDeBanca: {
                    id: data.dataDeBanca.id,
                    horaInicial: data.dataDeBanca.horaInicial,
                    horaFinal: data.dataDeBanca.horaFinal,
                    data: data.dataDeBanca.data,
                    confirmada: data.dataDeBanca.confirmada
                },
                processoId: data.processoId
            }));
            return bancas;
        } catch (error) {
            console.error('Erro ao buscar bancas membros:', error);
            throw error;
        }
    }

    static async getBancasParaMembro(): Promise<BancasMembroResponse> {
        try {
            const response = await api.get('/membros-banca/bancas');
            return response.data as BancasMembroResponse;
        } catch (error) {
            console.error('Erro ao buscar bancas para membro:', error);
            throw error;
        }
    }

    static async getBancasDoDiaMembro(isMembroDefinido?: boolean): Promise<BancaDoDia[]> {
        try {
            const params = {
                isMembroDefinido
            }
            if (isMembroDefinido) {
                const response = await api.get('/bancas/membro', { params });
                const bancas: BancaDoDia[] = response.data.map((data: any) => ({
                    processoId: data.processoId,
                    identificadorProcesso: data.identificadorProcesso,
                    editalProcesso: data.editalProcesso,
                    bancaId: data.bancaId,
                    status: BancaUtils.mapStringToStatusBanca(data.status as string),
                    tipoBanca: {
                        id: data.tipoBanca.id,
                        nome: BancaUtils.mapStringToTipoBancaNome(data.tipoBanca.nome)
                    },
                    salaDeBanca: {
                        id: data.salaDeBanca.id,
                        link: data.salaDeBanca.link
                    },
                    dataDeBanca: {
                        id: data.dataDeBanca.id,
                        horaInicial: data.dataDeBanca.horaInicial,
                        horaFinal: data.dataDeBanca.horaFinal,
                        data: data.dataDeBanca.data,
                        confirmada: data.dataDeBanca.confirmada
                    }
                }));

                return bancas;
            }
            else {
                const response = await api.get('/bancas/membro');
                const bancas: BancaDoDia[] = response.data.map((data: any) => ({
                    processoId: data.processoId,
                    identificadorProcesso: data.identificadorProcesso,
                    editalProcesso: data.editalProcesso,
                    bancaId: data.bancaId,
                    status: BancaUtils.mapStringToStatusBanca(data.status as string),
                    tipoBanca: {
                        id: data.tipoBanca.id,
                        nome: BancaUtils.mapStringToTipoBancaNome(data.tipoBanca.nome)
                    },
                    salaDeBanca: {
                        id: data.salaDeBanca.id,
                        link: data.salaDeBanca.link
                    },
                    dataDeBanca: {
                        id: data.dataDeBanca.id,
                        horaInicial: data.dataDeBanca.horaInicial,
                        horaFinal: data.dataDeBanca.horaFinal,
                        data: data.dataDeBanca.data,
                        confirmada: data.dataDeBanca.confirmada
                    }
                }));

                return bancas;
            }
        } catch (error) {
            throw error;
        }
    }

    static async getAtaBanca(processoId: string, bancaId: string, processoNome: string, bancaTipo: string): Promise<void> {
        try {
            const response = await api.get(`/processos/${processoId}/bancas/${bancaId}/ata/pdf`, {
                responseType: 'blob'
            });

            // Verifica se o conteúdo é realmente um ZIP
            const contentType = response.headers['content-type'];
            if (contentType !== 'application/zip' && contentType !== 'application/octet-stream') {
                throw new Error('Tipo de arquivo recebido não é ZIP');
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ata_${processoNome}_banca_${bancaTipo}.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Libera a URL do objeto após o download
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (error) {
            console.error('Erro ao baixar arquivo ZIP:', error);
            throw error;
        }
    }

    static async getAtaValidacaoAnteriorResumo(processoId: string): Promise<AtaValidacaoAnteriorResumoResponse> {
        const response = await api.get<AtaValidacaoAnteriorResumoResponse>(
            `/processos/${processoId}/atas/validacao-anterior/resumo`
        );
        return response.data;
    }

    static async getAtaValidacaoAnteriorPdf(processoId: string, processoNome: string): Promise<void> {
        const response = await api.get(`/processos/${processoId}/atas/validacao-anterior/pdf`, {
            responseType: 'blob'
        });

        const contentType = response.headers['content-type'];
        if (contentType !== 'application/pdf') {
            throw new Error('Tipo de arquivo recebido não é PDF');
        }

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ata_validacao_anterior_${processoNome}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
    }

    // GET all bancas for a given processo with button configurations
    static async getBancasNova(processoId: string, page: number = 0, size: number = 10): Promise<BancasNovaApiResponse> {
        try {
            const response = await api.get(
                `/processos/${processoId}/bancas?page=${page}&size=${size}`
            );
            
            if (response.status === 200) {
                const bancasRaw =
                    response.data?._embedded?.bancasNovaResponseList ??
                    response.data?.content ??
                    [];

                const pageData = response.data?.page
                    ? {
                        size: response.data.page.size ?? size,
                        totalElements: response.data.page.totalElements ?? bancasRaw.length,
                        totalPages: response.data.page.totalPages ?? 1,
                        number: response.data.page.number ?? page
                    }
                    : {
                        size: response.data?.size ?? size,
                        totalElements: response.data?.totalElements ?? bancasRaw.length,
                        totalPages: response.data?.totalPages ?? 1,
                        number: response.data?.number ?? page
                    };

                return {
                    _embedded: {
                        bancasNovaResponseList: (bancasRaw as BancaNovaResponse[]).map((banca: BancaNovaResponse) => ({
                            ...banca,
                            status: BancaUtils.mapStringToStatusBanca(banca.status as unknown as string),
                            membros: banca.membros.map((membro) => ({
                                ...membro,
                                statusMembro: BancaViewModelUtils.mapStatusMembroNome(membro.statusMembro as unknown as string)
                            }))
                        }))
                    },
                    page: pageData
                };
            } else {
                throw new Error(`Erro ao buscar bancas: ${response.statusText}`);
            }
        } catch (error) {
            console.error(`Erro ao buscar bancas para o processo ${processoId}:`, error);
            throw error;
        }
    }

    // GET all bancas for a given processo (legacy)
    static async getBancas(processoId: string): Promise<Banca[]> {
        try {
            const response = await api.get(`/processos/${processoId}/bancas`);
            if (response.status === 200) {
                return response.data?.map((banca: Banca) => ({
                    ...banca,
                    status: BancaUtils.mapStringToStatusBanca(banca.status as unknown as string),
                    membros: banca.membros.map((membro) => ({
                        ...membro,
                        statusMembro: BancaViewModelUtils.mapStatusMembroNome(membro.statusMembro as unknown as string)
                    }))
                })) as Banca[];
            } else {
                throw new Error(`Erro ao buscar bancas: ${response.statusText}`);
            }
        } catch (error) {
            console.error(`Erro ao buscar bancas para o processo ${processoId}:`, error);
            throw error;
        }
    }

    // GET banca by ID
    static async getBancaProcessoIdAndBancaId(processoId?: string, bancaId?: string): Promise<Banca> {
        try {
            if (processoId) {
                try {
                    const response = await api.get(`/processos/${processoId}/bancas/${bancaId}`);
                    const banca = response.data as Banca;
                    return {
                        ...banca,
                        status: BancaUtils.mapStringToStatusBanca(banca.status as unknown as string),
                        tipoBanca: {
                            id: banca.tipoBanca.id,
                            nome: banca.tipoBanca.nome
                        },
                        membros: banca.membros.map((membro) => ({
                            ...membro,
                            statusMembro: BancaViewModelUtils.mapStatusMembroNome(membro.statusMembro as unknown as string)
                        }))
                    };
                }
                catch (error) {
                    throw (error)
                }
            }
            else {
                try {
                    console.log("BancaID:" + bancaId)
                    const response = await api.get(`/processos/bancas/${bancaId}`);
                    const banca = response.data as Banca;
                    return {
                        ...banca,
                        status: BancaUtils.mapStringToStatusBanca(banca.status as unknown as string),
                        membros: banca.membros.map((membro) => ({
                            ...membro,
                            statusMembro: BancaViewModelUtils.mapStatusMembroNome(membro.statusMembro as unknown as string)
                        }))
                    };
                } catch (error) {
                    throw (error)
                }
            }

        } catch (error) {
            console.error(`Erro ao buscar banca para bancaId ${bancaId}:`, error);
            throw error;
        }
    }

    static async getBancaById(bancaId: string): Promise<Banca> {
        try {
            console.log("BancaID:" + bancaId)
            const response = await api.get(`/processos/bancas/${bancaId}`);
            const banca = response.data as Banca;
            return {
                ...banca,
                status: BancaUtils.mapStringToStatusBanca(banca.status as unknown as string),
                membros: banca.membros.map((membro) => ({
                    ...membro,
                    statusMembro: BancaViewModelUtils.mapStatusMembroNome(membro.statusMembro as unknown as string)
                }))
            };
        } catch (error: any) {
            throw error
        }
    }

    // POST create a new banca
    static async createBanca(processoId: string, tipoBanca: TipoBanca): Promise<Banca> {
        const body = {
            tipoBanca: {
                id: tipoBanca.id,
                nome: tipoBanca.nome
            }
        };

        const response = await api.post(`/processos/${processoId}/bancas`, body);

        const bancaCompleta: Banca = {
            ...response.data,
            membros: response.data.membros || [],
            dataDeBanca: response.data.dataDeBanca || {
                data: null,
                horaInicial: null,
                horaFinal: null,
                confirmada: null
            },
            status: StatusBanca.INDEFINIDA,
            salaDeBanca: response.data.salaDeBanca || {
                id: '',
                link: null
            }
        };

        return bancaCompleta;
    }

    // POST create a new banca with editais
    static async createBancaWithEditais(processoId: string, tipoBanca: TipoBanca, editais: Array<{editalId: string, numeroChamada?: number}>, formaRealizacao: 'PRESENCIAL' | 'REMOTO', hasIntegracao?: boolean): Promise<Banca> {
        const body = {
            tipoBanca: {
                id: tipoBanca.id,
                nome: tipoBanca.nome
            },
            editais: editais.map(e => ({
                editalId: e.editalId,
                numeroChamada: e.numeroChamada
            })),
            formaRealizacao,
            hasIntegracao: hasIntegracao || false
        };

        const response = await api.post(`/processos/${processoId}/bancas`, body);

        const bancaCompleta: Banca = {
            ...response.data,
            membros: response.data.membros || [],
            dataDeBanca: response.data.dataDeBanca || {
                data: null,
                horaInicial: null,
                horaFinal: null,
                confirmada: null
            },
            status: StatusBanca.INDEFINIDA,
            salaDeBanca: response.data.salaDeBanca || {
                id: '',
                link: null
            }
        };

        return bancaCompleta;
    }


    // PATCH update banca
    static async definirBanca(processoId: string, bancaId: string, membros: MembroBanca[]): Promise<{ data: Banca, status: number }> {
        const membrosReduzidos = membros.map(membro => ({
            id: membro.id,
            papelMembro: {
                id: membro.papelMembro.id,
                nome: membro.papelMembro.nome
            },
            vinculo: {
                id: membro.pessoa.vinculo.id,
                nome: membro.pessoa.vinculo.nome
            },
            isPresidente: membro.isPresidente || false
        }));

        const response = await api.patch(`/processos/${processoId}/bancas/${bancaId}/definicao`, { membros: membrosReduzidos });
        return { data: response.data, status: response.status };
    }




}

export class BancaViewModelUtils {
    static mapStatusMembroNome = (status: string): StatusMembroNome => {
        switch (status) {
            case 'CONSULTADO':
                return StatusMembroNome.CONSULTADO;
            case 'CONFIRMADO':
                return StatusMembroNome.CONFIRMADO;
            case 'DEFINIDO':
                return StatusMembroNome.DEFINIDO;
            case 'CONVOCADO':
                return StatusMembroNome.CONVOCADO;
            case 'CONVOCACAO_CONFIRMADA':
                return StatusMembroNome.CONVOCACAO_CONFIRMADA
            default:
                throw new Error(`Unknown StatusMembroNome: ${status}`);
        }
    };
}
